import classNames from 'classnames';
import React, { useCallback } from 'react';
import styles from './Checkbox.module.scss';

const CheckedSymbol = () => (
  <svg
    width="10"
    height="8"
    viewBox="0 0 10 8"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: 10, height: 8 }}
  >
    <path
      d="M9 1.33333L3.5 6.33333L1 4.0606"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IndeterminateSymbol = () => (
  <svg
    width="10"
    height="2"
    viewBox="0 0 10 2"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ width: 10, height: 2 }}
  >
    <path
      d="M8.5 1L1.5 1"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

export interface CheckboxProps extends React.HTMLProps<HTMLInputElement> {
  indeterminate?: boolean;

  onChangeChecked?: (checked: boolean, event: React.SyntheticEvent) => void;
}

const Checkbox: React.FC<CheckboxProps> = ({
  checked,
  children,
  className,
  disabled,
  indeterminate = false,
  label,
  style,
  onChange,
  onChangeChecked,
  ...props
}) => {
  const handleOnChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      onChange?.(event);
      onChangeChecked?.(event.target.checked, event);
    },
    [onChange, onChangeChecked]
  );

  const checkbox = (
    <span
      className={classNames(!label && className, styles.checkbox, {
        [styles.checked]: checked,
        [styles.disabled]: disabled,
        [styles.indeterminate]: !checked && indeterminate,
      })}
      role="checkbox"
      aria-checked={checked}
      style={style}
    >
      <input
        ref={input => {
          if (input) {
            input.indeterminate = !checked && indeterminate;
          }
        }}
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={handleOnChange}
        data-testid="checkbox"
        {...props}
      />
      <span className={styles.inner}>
        {checked && <CheckedSymbol />}
        {!checked && indeterminate && <IndeterminateSymbol />}
        <div className={styles.stateLayer}></div>
      </span>
    </span>
  );

  const labelContent = children ?? label;

  return labelContent ? (
    <label
      htmlFor={props.id}
      className={classNames(className, styles.label, {
        [styles.disabled]: disabled,
        [styles.checked]: checked,
        [styles.indeterminate]: indeterminate,
      })}
    >
      {checkbox}
      {labelContent}
    </label>
  ) : (
    checkbox
  );
};

Checkbox.displayName = 'Checkbox';

export default Checkbox;
